import React from "react";
import { Shield, Users, Clock, Leaf, MessageCircle, Star } from "lucide-react";
import { COMMITMENTS, TESTIMONIALS, buildWhatsAppLink } from "../mock/mock";

const iconMap = { shield: Shield, users: Users, clock: Clock, leaf: Leaf };

const APropos = () => {
  return (
    <section id="apropos" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        {/* About */}
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block bg-[#0B2545] text-[#FFC93C] text-xs font-black tracking-widest px-3 py-1 rounded-full">
              À PROPOS
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl font-black text-[#0B2545] leading-tight">
              Vivez la Guadeloupe autrement avec GwadaPlaisance
            </h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Depuis notre base au Gosier, nous vous accueillons pour découvrir
              le Grand Cul-de-Sac Marin, réserve marine classée et l&apos;un des plus beaux
              lagons des Caraïbes. Petits groupes, équipage local, matériel sécurisé :
              tout est pensé pour vous offrir une expérience authentique et inédite.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              Notre passion : partager les trésors cachés de la Guadeloupe, dans le respect
              de la nature et des traditions créoles. Bienvenue à bord !
            </p>

            <a
              href={buildWhatsAppLink("Bonjour, je souhaite plus d'informations sur GwadaPlaisance.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold px-5 py-3 rounded-xl shadow transition"
            >
              <MessageCircle className="h-5 w-5" fill="white" strokeWidth={0} />
              PARLONS DE VOTRE PROJET
            </a>
          </div>

          <div className="relative">
            <div className="absolute -top-4 -left-4 h-24 w-24 rounded-full bg-[#FFC93C]/70 blur-2xl" />
            <div className="absolute -bottom-6 -right-4 h-32 w-32 rounded-full bg-[#25D366]/40 blur-3xl" />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_gwada-qr-code/artifacts/q9dwjgfb_WhatsApp%20Image%202026-07-13%20at%2018.10.46%40.jpeg"
                alt="Notre bateau Ocely à la Marina du Gosier"
                className="block w-full h-auto"
              />
              {/* Discreet mask to hide old phone number on hull - blurred, feathered edges */}
              <div
                aria-hidden="true"
                className="absolute pointer-events-none"
                style={{
                  left: "49%",
                  top: "53.5%",
                  width: "22%",
                  height: "13%",
                  backgroundColor: "rgba(248, 246, 240, 0.55)",
                  backdropFilter: "blur(9px) saturate(1.05)",
                  WebkitBackdropFilter: "blur(9px) saturate(1.05)",
                  WebkitMaskImage:
                    "radial-gradient(ellipse at center, rgba(0,0,0,1) 40%, rgba(0,0,0,0.6) 65%, rgba(0,0,0,0) 100%)",
                  maskImage:
                    "radial-gradient(ellipse at center, rgba(0,0,0,1) 40%, rgba(0,0,0,0.6) 65%, rgba(0,0,0,0) 100%)",
                  borderRadius: "12px",
                }}
              />
            </div>
            <div className="absolute -bottom-4 left-6 bg-[#FFC93C] text-[#0B2545] font-black text-xs tracking-widest px-4 py-2 rounded-full shadow-lg">
              NOTRE BATEAU
            </div>
          </div>
        </div>

        {/* Commitments */}
        <div className="mt-16 bg-[#0B2545] rounded-3xl p-8 md:p-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {COMMITMENTS.map((c, i) => {
              const Icon = iconMap[c.icon] || Shield;
              return (
                <div key={i} className="flex items-start gap-4">
                  <div className="h-14 w-14 rounded-full bg-[#FFC93C]/15 border border-[#FFC93C]/40 flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6 text-[#FFC93C]" />
                  </div>
                  <div>
                    <div className="text-white font-black tracking-wide">{c.title}</div>
                    <div className="text-white/70 text-sm mt-1 leading-relaxed">
                      {c.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Testimonials */}
        <div className="mt-16">
          <div className="text-center mb-10">
            <span className="inline-block bg-[#FFC93C]/20 text-[#0B2545] text-xs font-black tracking-widest px-3 py-1 rounded-full">
              AVIS CLIENTS
            </span>
            <h3 className="mt-3 text-2xl md:text-3xl font-black text-[#0B2545]">
              Ils ont vécu l&apos;expérience
            </h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="bg-slate-50 border border-slate-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition"
              >
                <div className="flex gap-1">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-[#FFC93C] text-[#FFC93C]" />
                  ))}
                </div>
                <p className="mt-3 text-slate-700 italic leading-relaxed">“{t.text}”</p>
                <div className="mt-4 font-bold text-[#0B2545]">{t.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default APropos;
